<template>
  <Main
    v-if="!maxTablet || !tab"
    :tab="tab"
    :task="task"
    :feed="feed"
    :attachments="attachments"
    :users="users"
    :commentsMedia="commentsMedia.filter((media) => media.preview)"
    :canEdit="canEdit"
    :canLogTime="canLogTime"
    :canLogMaterial="canLogMaterial"
    :isArchived="isArchived"
    :readonly="readonly"
    @update="$emit('update', $event)"
    @logTime="$emit('logTime')"
    @showTimeLog="$emit('showTimeLog')"
    @logMaterial="$emit('logMaterial')"
    @showMaterialLog="$emit('showMaterialLog')"
  />
  <Comments
    v-else-if="tab === 'comments'"
    :task="task"
    :feed="feed"
    :users="users"
    :media="commentsMedia.filter((media) => media.preview)"
    :readonly="isArchived"
  />
  <Activity v-else-if="tab === 'activity'" :task="task" :feed="feed" />
  <Attachments v-else-if="tab === 'attachments'" :attachments="attachments" />
</template>

<script setup>
import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { isSupportedMedia } from "@/SharedComponents/Media";
import {
  Main,
  Attachments,
  Activity,
  Comments,
} from "@/SharedComponents/Tasks/Tabs";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";

const { maxTablet } = useBreakpoints();
const { t } = useI18n();

defineEmits([
  "update",
  "logTime",
  "showTimeLog",
  "logMaterial",
  "showMaterialLog",
]);

const props = defineProps({
  tab: { type: String, default: null },
  task: { type: Object, required: true },
  openTabs: { type: Boolean, required: false },
  feed: { type: Array, required: true },
  users: { type: Array, required: true },
  canEdit: { type: Boolean, default: false },
  canLogTime: { type: Boolean, default: false },
  canLogMaterial: { type: Boolean, default: false },
  isArchived: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
});

const commentsMedia = computed(() =>
  props.feed
    .map(
      (item) =>
        item.comment?.documents.map((document) => ({
          ...document,
          preview: isSupportedMedia(document.mime_type),
        })) || []
    )
    .flat()
);

const attachments = computed(() => {
  return [
    {
      id: "task",
      title: t("components.task_details.attachments.task"),
      items:
        props.task.problem?.photos.map((photo) => ({
          ...photo,
          name: photo.name ?? "untitled." + photo.path.split(".").pop(),
          preview: true,
        })) || [],
    },
    {
      id: "comments",
      title: t("components.task_details.attachments.comments"),
      items: commentsMedia.value,
    },
    {
      id: "material",
      title: t("components.task_details.attachments.material"),
      items: props.task.material?.reduce((acc, log) => {
        return [
          ...acc,
          ...log.documents.map((document) => ({
            ...document,
            preview: isSupportedMedia(document.mime_type),
          })),
        ];
      }, []),
    },
    {
      id: "space",
      title: t("components.task_details.attachments.space"),
      items:
        usePage()
          .props.notes?.filter((note) => note.file)
          .map((note) => ({
            ...note.file,
            preview: isSupportedMedia(note.file.mime_type),
          }))
          .flat() || [],
    },
  ];
});
</script>
